import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Switch,
  Skeleton,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { MainLayout } from '../../layouts/MainLayout';
import {
  masterDataService,
  type RiskCategoryData,
  type MAMethodData,
  type CountryData,
} from '../../services/api/masterDataService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export function MasterDataAdminPage() {
  const [tabValue, setTabValue] = useState(0);
  const [riskCategories, setRiskCategories] = useState<RiskCategoryData[]>([]);
  const [maMethods, setMAMethods] = useState<MAMethodData[]>([]);
  const [countries, setCountries] = useState<CountryData[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editType, setEditType] = useState<'risk' | 'method' | 'country'>('risk');
  const [selectedItem, setSelectedItem] = useState<RiskCategoryData | MAMethodData | CountryData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    apiSource: '',
  });

  // データ取得
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [riskCategoriesData, maMethodsData, countriesData] = await Promise.all([
        masterDataService.getRiskCategories(),
        masterDataService.getMAMethods(),
        masterDataService.getCountries(),
      ]);
      setRiskCategories(riskCategoriesData);
      setMAMethods(maMethodsData);
      setCountries(countriesData);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'データの取得に失敗しました',
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenAddDialog = (type: 'risk' | 'method' | 'country') => {
    setEditType(type);
    setSelectedItem(null);
    setFormData({ code: '', name: '', description: '', apiSource: '' });
    setEditDialogOpen(true);
  };

  const handleOpenEditDialog = (
    type: 'risk' | 'method' | 'country',
    item: RiskCategoryData | MAMethodData | CountryData
  ) => {
    setEditType(type);
    setSelectedItem(item);
    setFormData({
      code: item.code,
      name: item.name,
      description: 'description' in item ? item.description : '',
      apiSource: 'apiSource' in item ? item.apiSource : '',
    });
    setEditDialogOpen(true);
  };

  const handleOpenDeleteDialog = (
    type: 'risk' | 'method' | 'country',
    item: RiskCategoryData | MAMethodData | CountryData
  ) => {
    setEditType(type);
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  const handleToggleActive = async (
    type: 'risk' | 'method' | 'country',
    id: string,
    isActive: boolean
  ) => {
    try {
      if (type === 'risk') {
        await masterDataService.updateRiskCategory(id, { isActive });
        setRiskCategories((prev) =>
          prev.map((item) => (item.id === id ? { ...item, isActive } : item))
        );
      } else if (type === 'method') {
        await masterDataService.updateMAMethod(id, { isActive });
        setMAMethods((prev) =>
          prev.map((item) => (item.id === id ? { ...item, isActive } : item))
        );
      } else {
        await masterDataService.updateCountry(id, { isActive });
        setCountries((prev) =>
          prev.map((item) => (item.id === id ? { ...item, isActive } : item))
        );
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : '更新に失敗しました',
      });
    }
  };

  const handleSave = async () => {
    if (!formData.code || !formData.name) return;

    setIsSubmitting(true);
    try {
      if (selectedItem) {
        // 更新
        if (editType === 'risk') {
          const updated = await masterDataService.updateRiskCategory(selectedItem.id, {
            code: formData.code,
            name: formData.name,
            description: formData.description,
          });
          setRiskCategories((prev) =>
            prev.map((item) => (item.id === selectedItem.id ? updated : item))
          );
        } else if (editType === 'method') {
          const updated = await masterDataService.updateMAMethod(selectedItem.id, {
            code: formData.code,
            name: formData.name,
            description: formData.description,
          });
          setMAMethods((prev) =>
            prev.map((item) => (item.id === selectedItem.id ? updated : item))
          );
        } else {
          const updated = await masterDataService.updateCountry(selectedItem.id, {
            code: formData.code,
            name: formData.name,
            apiSource: formData.apiSource,
          });
          setCountries((prev) =>
            prev.map((item) => (item.id === selectedItem.id ? updated : item))
          );
        }
        setMessage({ type: 'success', text: `${formData.name} を更新しました` });
      } else {
        // 新規作成
        if (editType === 'risk') {
          const created = await masterDataService.createRiskCategory({
            code: formData.code,
            name: formData.name,
            description: formData.description,
          });
          setRiskCategories((prev) => [...prev, created]);
        } else if (editType === 'method') {
          const created = await masterDataService.createMAMethod({
            code: formData.code,
            name: formData.name,
            description: formData.description,
          });
          setMAMethods((prev) => [...prev, created]);
        } else {
          const created = await masterDataService.createCountry({
            code: formData.code,
            name: formData.name,
            apiSource: formData.apiSource,
          });
          setCountries((prev) => [...prev, created]);
        }
        setMessage({ type: 'success', text: `${formData.name} を追加しました` });
      }

      setEditDialogOpen(false);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : '保存に失敗しました',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedItem) return;

    setIsSubmitting(true);
    try {
      if (editType === 'risk') {
        await masterDataService.deleteRiskCategory(selectedItem.id);
        setRiskCategories((prev) => prev.filter((item) => item.id !== selectedItem.id));
      } else if (editType === 'method') {
        await masterDataService.deleteMAMethod(selectedItem.id);
        setMAMethods((prev) => prev.filter((item) => item.id !== selectedItem.id));
      } else {
        await masterDataService.deleteCountry(selectedItem.id);
        setCountries((prev) => prev.filter((item) => item.id !== selectedItem.id));
      }

      setMessage({ type: 'success', text: `${selectedItem.name} を削除しました` });
      setDeleteDialogOpen(false);
      setSelectedItem(null);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : '削除に失敗しました',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDialogTitle = () => {
    const action = selectedItem ? '編集' : '追加';
    const type =
      editType === 'risk'
        ? 'リスクカテゴリ'
        : editType === 'method'
        ? 'M&A手法'
        : '対象国・地域';
    return `${type}を${action}`;
  };

  // ローディングスケルトン
  const TableSkeleton = () => (
    <TableBody>
      {[1, 2, 3, 4, 5].map((i) => (
        <TableRow key={i}>
          <TableCell><Skeleton width={24} /></TableCell>
          <TableCell><Skeleton width={80} /></TableCell>
          <TableCell><Skeleton width={120} /></TableCell>
          <TableCell><Skeleton width={200} /></TableCell>
          <TableCell><Skeleton width={40} /></TableCell>
          <TableCell><Skeleton width={40} /></TableCell>
          <TableCell><Skeleton width={80} /></TableCell>
        </TableRow>
      ))}
    </TableBody>
  );

  return (
    <MainLayout>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1" fontWeight={500}>
            マスターデータ管理
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            disabled={isLoading}
          >
            更新
          </Button>
        </Box>

        {/* Message */}
        {message && (
          <Alert
            severity={message.type}
            sx={{ mb: 3 }}
            onClose={() => setMessage(null)}
          >
            {message.text}
          </Alert>
        )}

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
            <Tab label="リスクカテゴリ" />
            <Tab label="M&A手法" />
            <Tab label="対象国・地域" />
          </Tabs>
        </Paper>

        {/* Risk Categories Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenAddDialog('risk')}
            >
              カテゴリを追加
            </Button>
          </Box>
          <Paper elevation={1}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell width={40}></TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>コード</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>名称</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>説明</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>項目数</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>有効</TableCell>
                    <TableCell sx={{ fontWeight: 500 }} align="center">
                      操作
                    </TableCell>
                  </TableRow>
                </TableHead>
                {isLoading ? (
                  <TableSkeleton />
                ) : (
                  <TableBody>
                    {riskCategories.map((cat) => (
                      <TableRow key={cat.id} hover>
                        <TableCell>
                          <DragIcon sx={{ color: 'text.secondary', cursor: 'grab' }} />
                        </TableCell>
                        <TableCell>
                          <Chip label={cat.code} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>{cat.name}</TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{cat.description}</TableCell>
                        <TableCell>{cat.itemCount}</TableCell>
                        <TableCell>
                          <Switch
                            checked={cat.isActive}
                            onChange={(e) => handleToggleActive('risk', cat.id, e.target.checked)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEditDialog('risk', cat)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDeleteDialog('risk', cat)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {riskCategories.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography color="text.secondary">
                            リスクカテゴリがありません
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Paper>
        </TabPanel>

        {/* M&A Methods Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenAddDialog('method')}
            >
              手法を追加
            </Button>
          </Box>
          <Paper elevation={1}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell width={40}></TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>コード</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>名称</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>説明</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>有効</TableCell>
                    <TableCell sx={{ fontWeight: 500 }} align="center">
                      操作
                    </TableCell>
                  </TableRow>
                </TableHead>
                {isLoading ? (
                  <TableSkeleton />
                ) : (
                  <TableBody>
                    {maMethods.map((method) => (
                      <TableRow key={method.id} hover>
                        <TableCell>
                          <DragIcon sx={{ color: 'text.secondary', cursor: 'grab' }} />
                        </TableCell>
                        <TableCell>
                          <Chip label={method.code} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>{method.name}</TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{method.description}</TableCell>
                        <TableCell>
                          <Switch
                            checked={method.isActive}
                            onChange={(e) => handleToggleActive('method', method.id, e.target.checked)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEditDialog('method', method)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDeleteDialog('method', method)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {maMethods.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography color="text.secondary">
                            M&A手法がありません
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Paper>
        </TabPanel>

        {/* Countries Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenAddDialog('country')}
            >
              国・地域を追加
            </Button>
          </Box>
          <Paper elevation={1}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 500 }}>コード</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>名称</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>法令データソース</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>法令数</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>有効</TableCell>
                    <TableCell sx={{ fontWeight: 500 }} align="center">
                      操作
                    </TableCell>
                  </TableRow>
                </TableHead>
                {isLoading ? (
                  <TableSkeleton />
                ) : (
                  <TableBody>
                    {countries.map((country) => (
                      <TableRow key={country.id} hover>
                        <TableCell>
                          <Chip label={country.code} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>{country.name}</TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{country.apiSource}</TableCell>
                        <TableCell>{country.lawCount.toLocaleString()}+</TableCell>
                        <TableCell>
                          <Switch
                            checked={country.isActive}
                            onChange={(e) => handleToggleActive('country', country.id, e.target.checked)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEditDialog('country', country)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDeleteDialog('country', country)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {countries.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography color="text.secondary">
                            対象国・地域がありません
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Paper>
        </TabPanel>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{getDialogTitle()}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="コード"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              fullWidth
              required
              helperText="英数字とアンダースコアのみ使用可能"
            />
            <TextField
              label="名称"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            {editType !== 'country' ? (
              <TextField
                label="説明"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />
            ) : (
              <TextField
                label="法令データソース"
                value={formData.apiSource}
                onChange={(e) => setFormData({ ...formData, apiSource: e.target.value })}
                fullWidth
                helperText="例: e-Gov法令API v2, 手動管理"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>キャンセル</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSubmitting || !formData.code || !formData.name}
          >
            {isSubmitting ? <CircularProgress size={24} /> : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>削除の確認</DialogTitle>
        <DialogContent>
          <Typography>
            「{selectedItem?.name}」を削除してもよろしいですか？
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            この操作は取り消せません。関連するデータにも影響する可能性があります。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={24} /> : '削除'}
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
}
